import type { Trip } from '../types/database';
import { parseGeoPoint } from '../utils/formatters';
import {
  defaultEndTimeFromPickup,
  timeStringToMinutes,
} from '../utils/scheduleConflict';
import type { ScheduleLegInput } from './types';
import { fallbackGeoPoint } from './jobLegConverters';

export type TripForFixedBlock = Pick<
  Trip,
  | 'id'
  | 'pickup_time'
  | 'end_time'
  | 'pickup_address'
  | 'dropoff_address'
  | 'pickup_geopoint'
  | 'dropoff_geopoint'
  | 'vehicle_id'
  | 'job_id'
  | 'status'
>;

/** Convert a published trip into a read-only schedule leg (EXT.SCHED.11). */
export function tripToFixedScheduleLeg(trip: TripForFixedBlock): ScheduleLegInput | null {
  if (!trip.vehicle_id) return null;
  if (!['scheduled', 'dispatched', 'in_progress'].includes(trip.status)) return null;

  const pickup = trip.pickup_geopoint
    ? parseGeoPoint(trip.pickup_geopoint)
    : fallbackGeoPoint();
  const dropoff = trip.dropoff_geopoint
    ? parseGeoPoint(trip.dropoff_geopoint)
    : pickup;
  if (!pickup || !dropoff) return null;

  const start = timeStringToMinutes(trip.pickup_time);
  const endRaw = trip.end_time || defaultEndTimeFromPickup(trip.pickup_time);
  const end = timeStringToMinutes(endRaw);
  const duration = Math.max(1, Math.round(end - start));

  return {
    jobId: trip.job_id ?? `published:${trip.id}`,
    legId: `fixed:${trip.id}`,
    legType: 'transfer',
    isFixed: true,
    label: `Published · ${trip.pickup_address}`,
    pickup,
    dropoff,
    earliestStartMinutes: start,
    latestStartMinutes: start,
    serviceDurationMinutes: duration,
  };
}

export function groupFixedLegsByVehicle(
  trips: TripForFixedBlock[]
): Record<string, ScheduleLegInput[]> {
  const byVehicle: Record<string, ScheduleLegInput[]> = {};
  for (const trip of trips) {
    const leg = tripToFixedScheduleLeg(trip);
    if (!leg || !trip.vehicle_id) continue;
    const list = byVehicle[trip.vehicle_id] ?? [];
    list.push(leg);
    byVehicle[trip.vehicle_id] = list;
  }
  for (const vehicleId of Object.keys(byVehicle)) {
    byVehicle[vehicleId].sort((a, b) => a.earliestStartMinutes - b.earliestStartMinutes);
  }
  return byVehicle;
}
